export type AdoptionListingStatus =
  | 'DRAFT'
  | 'PUBLISHED'
  | 'ADOPTED'
  | 'CLOSED'
  | 'HIDDEN'
  | 'REMOVED';
export type AdoptionSpecies = 'DOG' | 'CAT' | 'BIRD' | 'RABBIT' | 'FISH' | 'OTHER';

export const ADOPTION_STATUS_OPTIONS: AdoptionListingStatus[] = [
  'DRAFT',
  'PUBLISHED',
  'ADOPTED',
  'CLOSED',
  'HIDDEN',
  'REMOVED',
];
export const ADOPTION_SPECIES_OPTIONS: AdoptionSpecies[] = [
  'DOG',
  'CAT',
  'BIRD',
  'RABBIT',
  'FISH',
  'OTHER',
];

/** What the poster chose to say about the animal, as a person reads it. */
export const ADOPTION_STATUS_LABELS: Record<AdoptionListingStatus, string> = {
  DRAFT: 'Draft',
  PUBLISHED: 'Live',
  ADOPTED: 'Adopted',
  CLOSED: 'Withdrawn',
  HIDDEN: 'Hidden',
  REMOVED: 'Removed',
};

/** Server-side sort keys. Anything not in this list is not offered in the UI. */
export const ADOPTION_SORT_FIELDS = [
  'created_at',
  'published_at',
  'city',
  'species',
  'status',
  'request_count',
] as const;

export type AdoptionPoster = {
  id: string;
  name: string | null;
  phone: string | null;
  avatar_path?: string | null;
};

export type AdoptionPhoto = {
  id: string;
  is_primary: boolean;
  display_order?: number;
  file_path: string;
  thumb_path?: string;
  uploaded_at?: string;
};

export type AdoptionListItem = {
  id: string;
  species: AdoptionSpecies;
  breed: string | null;
  gender: string;
  age_value: number | null;
  age_unit: string | null;
  age_label: string | null;
  city: string;
  state: string | null;
  status: AdoptionListingStatus;
  vaccinated: boolean | null;
  sterilized: boolean | null;
  poster: AdoptionPoster;
  primary_photo_path: string | null;
  primary_photo_thumb_path: string | null;
  photo_count: number;
  request_count: number;
  /** Whether the poster ever proved the number adopters are told to ring. */
  phone_verified: boolean;
  published_at: string | null;
  created_at: string;
};

export type AdoptionListResponse = {
  items: AdoptionListItem[];
  total: number;
  page: number;
  page_size: number;
};

export type AdoptionRequestRow = {
  id: string;
  status: string;
  created_at: string;
  adopter: AdoptionPoster;
  payment: {
    status: string;
    amount_inr: string;
    captured_at: string | null;
    created_at?: string;
  } | null;
};

/** One admin decision on a listing, with the reason the poster was given. */
export type AdoptionModerationAudit = {
  id: string;
  from_status: AdoptionListingStatus;
  to_status: AdoptionListingStatus;
  reason: string;
  changed_at: string;
  changed_by_name: string | null;
};

export type AdoptionDetail = AdoptionListItem & {
  health_status: string | null;
  good_with: string | null;
  description: string | null;
  phone_verified_at: string | null;
  adopted_at: string | null;
  updated_at: string;
  contact: {
    contact_name: string;
    contact_phone: string;
    address_line: string | null;
    pincode: string | null;
    latitude: string | null;
    longitude: string | null;
  };
  photos: AdoptionPhoto[];
  requests: AdoptionRequestRow[];
  /** What the platform has collected in contact fees on this listing. */
  fees_collected_inr: string;
  moderation_audits: AdoptionModerationAudit[];
};

/** Tiles on the list, each of which is also a filter it can apply. */
export type AdoptionCounts = {
  total: number;
  published: number;
  draft: number;
  hidden: number;
  removed: number;
  adopted: number;
  closed: number;
  published_without_photo: number;
  published_unverified_phone: number;
  confirmed_requests: number;
};

export type AdoptionListFilters = {
  page?: number;
  page_size?: number;
  q?: string;
  status?: AdoptionListingStatus;
  /** Comma-joined, so one segment can cover several statuses. */
  statuses?: string;
  species?: string;
  gender?: string;
  city?: string;
  poster_user_id?: string;
  /** '' = either, 'true' / 'false' = that side only. Matches the URL-state tri-state. */
  has_photos?: string;
  phone_verified?: string;
  has_requests?: string;
  vaccinated?: string;
  sterilized?: string;
  /** Inclusive created-date range, YYYY-MM-DD. */
  from?: string;
  to?: string;
};

export type AdoptionModerationAction = 'hide' | 'unhide' | 'remove';

export type AdoptionBulkResult = {
  changed: number;
  /** Listings the action did not apply to, and why — never silently dropped. */
  skipped: Array<{ id: string; reason: string }>;
};

/**
 * One person who enquired, and whether the platform owes them their fee back.
 *
 * `can_refund` and `blocked_reason` are computed server-side and travel with the
 * row on purpose: a console deciding for itself whether a refund is allowed is how
 * you ship a button that 409s.
 */
export type AdoptionParticipant = {
  request_id: string;
  adopter_user_id: string;
  adopter_name: string | null;
  adopter_phone: string | null;
  request_status: string;
  paid_at: string | null;
  payment_id: string | null;
  amount_inr: string | null;
  refunded_inr: string | null;
  /** The one who took the pet. They keep their fee. */
  is_adopter: boolean;
  can_refund: boolean;
  blocked_reason: string | null;
};

export type AdoptionRefundView = {
  listing_id: string;
  listing_status: string;
  /** False while the listing is live — nobody has missed out on the pet yet. */
  is_refundable_stage: boolean;
  adopted_by_request_id: string | null;
  participants: AdoptionParticipant[];
  outstanding_inr: string;
};

export type AdoptionRefundAllResult = {
  view: AdoptionRefundView;
  refunded: string[];
  failed: Array<{ request_id: string; message: string }>;
};
